import React, { useState, useEffect } from 'react';
import axios from 'axios';
import CertificateSection from './CertificateSection';
import paymentQrImage from '../utility/Trekverse_Payments.jpeg';
import { API_BASE_URL } from '../config';
import './CourseDetail.css';

const CourseDetail = ({ course, onClose, onNavigate }) => {
  const [enrollStep, setEnrollStep] = useState('details');
  const [errorMessage, setErrorMessage] = useState('');

  // States for detailed course data
  const [detailedCourse, setDetailedCourse] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(true);

  // Fetch course details
  useEffect(() => {
    const fetchCourseDetails = async () => {
      try {
        const response = await axios.get(
          `${API_BASE_URL}/api/courses/programs/${course.id}/`
        );

        setDetailedCourse(response.data);
      } catch (error) {
        console.error(
          'Error fetching detailed course data:',
          error
        );

        setErrorMessage(
          'Could not load course curriculum. Please try again.'
        );
      } finally {
        setLoadingDetails(false);
      }
    };

    if (course && course.id) {
      fetchCourseDetails();
    }
  }, [course]);

  if (!course) return null;

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleInitialEnrollClick = () => {
    const token = sessionStorage.getItem('access_token');

    if (!token) {
      onClose();
      onNavigate('login');
      return;
    }

    setEnrollStep('options');
  };

  const handlePartialPayment = async () => {
    setErrorMessage('');

    const token = sessionStorage.getItem('access_token');

    try {
      await axios.post(
        `${API_BASE_URL}/api/courses/enroll/`,
        { course: course.id },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      setEnrollStep('admin_info');
    } catch (error) {
      console.error('Error:', error);

      if (error.response?.status === 401) {
        setErrorMessage(
          'Your session has expired. Redirecting to login...'
        );

        setTimeout(() => {
          localStorage.clear();
          onClose();
          onNavigate('login');
        }, 2000);

        return;
      }

      if (error.response?.data) {
        const data = error.response.data;

        const errorText =
          data.detail ||
          (data.non_field_errors &&
            data.non_field_errors[0]) ||
          'Could not process enrollment.';

        setErrorMessage(errorText);
      } else {
        setErrorMessage(
          'A network error occurred. Please try again later.'
        );
      }
    }
  };

  const handleFullPayment = () => {
    setEnrollStep('full_payment');
  };

  // Use fetched detailed data if available
  const displayData = detailedCourse || course;

  return (
    <div
      className="course-modal-overlay"
      onClick={handleOverlayClick}
    >
      <div className="course-modal">
        <div className="trek-course-header">
          <h2>
            {displayData.title || displayData.name}
          </h2>

          <button
            className="close-button"
            onClick={onClose}
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        <div className="trek-course-content">

          {/* VIEW 1: COURSE DETAILS */}
          {enrollStep === 'details' && (
            <>
              {loadingDetails ? (
                <div
                  className="loading-spinner text-center"
                  style={{ padding: '40px' }}
                >
                  <p>
                    Loading course curriculum...
                  </p>
                </div>
              ) : (
                <>
                  <div className="course-section">
                    <h3>Course Overview</h3>

                    <p>
                      {displayData.overview ||
                        displayData.description}
                    </p>
                  </div>

                  <div className="course-meta">
                    <div className="meta-box">
                      <span className="meta-label">
                        Duration
                      </span>

                      <span className="meta-value">
                        {displayData.duration
                          ?.trim()
                          .replace(/\n/g, ' ')}
                      </span>
                    </div>

                    <div className="meta-box">
                      <span className="meta-label">
                        Level
                      </span>

                      <span className="meta-value">
                        {displayData.level
                          ?.trim()
                          .replace(/\n/g, ' ')}
                      </span>
                    </div>

                    <div className="meta-box">
                      <span className="meta-label">
                        Students
                      </span>

                      <span className="meta-value">
                        {(displayData.students ||
                          '0')}
                        +
                      </span>
                    </div>
                  </div>

                  <div className="course-section">
                    <h3>
                      Course Curriculum
                    </h3>

                    <div className="curriculum-list">
                      {displayData.modules &&
                        displayData.modules.length >
                        0 ? (
                        displayData.modules.map(
                          (
                            moduleItem,
                            index
                          ) => (
                            <div
                              key={
                                moduleItem.id ||
                                index
                              }
                              className="module-item"
                            >
                              <div className="module-header">
                                <span className="module-number">
                                  {index + 1}
                                </span>

                                <h4>
                                  {
                                    moduleItem.title
                                  }
                                </h4>
                              </div>

                              <div className="topics-container">
                                {moduleItem.lessons &&
                                  moduleItem.lessons.map(
                                    (
                                      lesson
                                    ) => (
                                      <span
                                        key={
                                          lesson.id
                                        }
                                        className="topic-pill"
                                      >
                                        {
                                          lesson.title
                                        }
                                      </span>
                                    )
                                  )}
                              </div>
                            </div>
                          )
                        )
                      ) : (
                        <p>
                          Curriculum details
                          are currently being
                          updated by the admin.
                        </p>
                      )}
                    </div>
                  </div>
                </>
              )}

              <CertificateSection />

              <div className="modal-actions">
                <button
                  className="enroll-btn"
                  onClick={
                    handleInitialEnrollClick
                  }
                >
                  Enroll in This Course
                </button>

                <button
                  className="cancel-btn"
                  onClick={onClose}
                >
                  Cancel
                </button>
              </div>
            </>
          )}

          {/* VIEW 2: PAYMENT OPTIONS */}
          {enrollStep === 'options' && (
            <div className="payment-flow-container">
              <h3
                style={{
                  textAlign: 'center',
                  marginBottom: '20px',
                  color: '#2c3e50',
                }}
              >
                Choose Your Payment Plan
              </h3>

              {errorMessage && (
                <div
                  style={{
                    backgroundColor:
                      '#fef2f2',
                    border:
                      '1px solid #ef4444',
                    color: '#b91c1c',
                    padding:
                      '12px 16px',
                    borderRadius:
                      '6px',
                    marginBottom:
                      '20px',
                    display: 'flex',
                    alignItems:
                      'center',
                    fontSize:
                      '0.95rem',
                    fontWeight:
                      '500',
                  }}
                >
                  {errorMessage}
                </div>
              )}

              <div className="payment-options-grid">
                <div className="payment-card">
                  <h4>Pay in Full</h4>

                  <p className="price-tag">
                    ₹{course.price}
                  </p>

                  <p className="payment-desc">
                    Get instant access to all
                    course materials
                    immediately.
                  </p>

                  <button
                    className="enroll-btn full-width"
                    onClick={
                      handleFullPayment
                    }
                  >
                    Proceed to Payment
                  </button>
                </div>

                <div className="payment-card">
                  <h4>
                    Partial Payment
                  </h4>

                  <p className="price-tag">
                    Installments
                  </p>

                  <p className="payment-desc">
                    Pay manually in easy
                    installments. Requires
                    Admin approval.
                  </p>

                  <button
                    className="enroll-btn partial-btn full-width"
                    onClick={
                      handlePartialPayment
                    }
                  >
                    Request Installment
                    Plan
                  </button>
                </div>
              </div>

              <button
                className="back-text-btn"
                onClick={() =>
                  setEnrollStep(
                    'details'
                  )
                }
              >
                ← Back to Course Details
              </button>
            </div>
          )}

          {/* VIEW 3: ADMIN INFO */}
          {enrollStep === 'admin_info' && (
            <div className="payment-flow-container text-center">
              <h3
                style={{
                  color: '#2c3e50',
                }}
              >
                Request Sent Successfully
              </h3>

              <p
                style={{
                  margin: '15px 0',
                  color: '#555',
                  lineHeight: '1.6',
                }}
              >
                Your request for partial
                payment has been recorded.
                Please contact admin.
              </p>

              <div className="admin-contact-card">
                <p>
                  <strong>
                    Contact Name:
                  </strong>{' '}
                  TrekVerse Admin
                </p>

                <p>
                  <strong>
                    Phone Number:
                  </strong>{' '}
                  +91 6303462806
                </p>

                <p>
                  <strong>Email:</strong>{' '}
                  trekverse5@gmail.com
                </p>
              </div>

              <button
                className="enroll-btn"
                style={{
                  marginTop: '20px',
                }}
                onClick={onClose}
              >
                Return to Dashboard
              </button>
            </div>
          )}

          {/* VIEW 4: FULL PAYMENT */}
          {enrollStep === 'full_payment' && (
            <div className="payment-flow-container text-center qr-payment-section">
              <h3 style={{ color: '#2c3e50', marginBottom: '20px' }}>
                Complete Your Payment
              </h3>

              <div className="qr-container">
                <img 
                  src={paymentQrImage} 
                  alt="Payment QR Code" 
                  className="payment-qr-image" 
                />
              </div>

              <div className="payment-instructions">
                <p>After payment, send the payment screenshot to the Counsellor.</p>
              </div>

              <div className="modal-actions" style={{ justifyContent: 'center', marginTop: '30px' }}>
                <button
                  className="enroll-btn"
                  onClick={() => {
                    onClose();
                    onNavigate('home');
                  }}
                >
                  Back to Home
                </button>
              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default CourseDetail;